import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "Email address is required.")
    .email("Please enter a valid email address."),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordProps {
  email: string;
  onSubmit: (email: string) => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

export default function ForgotPassword({
  email,
  onSubmit,
  onBack,
  loading,
  error,
}: ForgotPasswordProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email },
  });

  const onFormSubmit = (data: ForgotPasswordFormValues) => {
    onSubmit(data.email);
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="btn btn-link p-0 mb-4 text-muted d-flex align-items-center gap-1"
        aria-label="Go back"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <h1 className="h3 fw-bold text-dark mb-1">Forgot your password?</h1>
      <p className="text-muted mb-4">
        Enter your email address and we&apos;ll send you a link to reset your
        password.
      </p>

      <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
        <div className="mb-3">
          <label htmlFor="reset-email" className="form-label fw-medium">
            Email address
          </label>
          <input
            id="reset-email"
            type="email"
            autoComplete="email"
            autoFocus
            className={`form-control form-control-lg${
              errors.email ? " is-invalid" : ""
            }`}
            placeholder="you@example.com"
            disabled={loading}
            {...register("email")}
          />
          {errors.email && (
            <div className="invalid-feedback">{errors.email.message}</div>
          )}
        </div>

        {error && (
          <div className="alert alert-danger py-2 mb-3" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-lg w-100"
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Sending...
            </>
          ) : (
            "Send Reset Link"
          )}
        </button>
      </form>
    </div>
  );
}
